import React, { useState, useMemo } from "react";
import DSButton from "../../system-design/DSButton";
import DSChip from "../../system-design/DSChip";
import { useAllClocksTagsQuery } from "../../../components/hooks";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSTextField from "../../system-design/DSTextField";
import DSIconButton from "../../system-design/DSIconButton";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import CloseIcon from "@mui/icons-material/Close";

interface TagSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
}

const TagSelectionModal: React.FC<TagSelectionModalProps> = ({
  isOpen,
  onClose,
  selectedTags,
  onTagsChange,
}) => {
  const [internalSelectedTags, setInternalSelectedTags] =
    useState<string[]>(selectedTags);
  const [searchText, setSearchText] = useState<string>("");
  const { data: allTags = [], isLoading: tagsLoading } =
    useAllClocksTagsQuery();

  // Filter available tags based on search text
  const filteredTags = useMemo(() => {
    if (!allTags) return [];

    const availableTags = allTags.filter(
      (tag) => !internalSelectedTags.includes(tag),
    );

    if (!searchText.trim()) return availableTags;

    return availableTags.filter((tag) =>
      tag.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [allTags, internalSelectedTags, searchText]);

  const handleApply = () => {
    onTagsChange(internalSelectedTags);
    onClose();
  };

  const handleCancel = () => {
    setInternalSelectedTags(selectedTags);
    onClose();
  };

  React.useEffect(() => {
    setInternalSelectedTags(selectedTags);
  }, [selectedTags]);

  return (
    <DSDialog open={isOpen} onClose={handleCancel} fullWidth maxWidth="sm">
      <DSDialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        Select Tags
        <DSIconButton onClick={handleCancel} aria-label="close">
          <CloseIcon />
        </DSIconButton>
      </DSDialogTitle>
      <DSDialogContent>
        <DSBox sx={{ mb: "16px", mt: 1 }}>
          <DSTextField
            placeholder="Search tags..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            fullWidth
          />
        </DSBox>
        <DSBox
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            mb: "16px",
          }}
        >
          {internalSelectedTags.map((tag, index) => (
            <DSChip
              key={index}
              label={tag}
              onDelete={() => {
                setInternalSelectedTags(
                  internalSelectedTags.filter((t) => t !== tag),
                );
              }}
            />
          ))}
        </DSBox>
        <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {tagsLoading ? (
            <DSCircularProgress size={20} />
          ) : filteredTags.length === 0 ? (
            <DSTypography variant="body2" color="textSecondary">
              No matching tags
            </DSTypography>
          ) : (
            filteredTags.map((tag, index) => (
              <DSChip
                key={index}
                label={tag}
                onClick={() => {
                  setInternalSelectedTags([...internalSelectedTags, tag]);
                }}
                style={{ cursor: "pointer" }}
              />
            ))
          )}
        </DSBox>
      </DSDialogContent>
      <DSDialogActions>
        <DSButton variant="outlined" color="secondary" onClick={handleCancel}>
          Cancel
        </DSButton>
        <DSButton variant="contained" color="primary" onClick={handleApply}>
          Apply
        </DSButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default TagSelectionModal;
